/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import * as Sentry from '@sentry/browser';
import { Alchemy } from '@blend/alchemy';
import { useEffect, useState, useRef } from 'react';
import { setAppElement } from 'react-modal';
import { MeContext } from 'blb-contexts/Me';
import { theme as grommetTheme } from 'blb-components/GrommetComponents';
import { UnauthenticatedApp } from 'blb-components/UnauthenticatedApp';
import { AuthenticatedApp } from 'blb-components/AuthenticatedApp';
import { getMe } from 'blb-api/auth';
import { redirectToIdentityLoginUI } from 'blb-api/auth';
import { getConfig } from 'blb-utils/config';
import { useLocation } from 'react-router-dom';
import {
  clearAllAuthedUsers,
  getPrimarySessionPrincipalId,
  getSecondarySessionPrincipalId,
  isPrimarySessionSet,
  isSecondarySessionSet,
  setPrimaryAuthedUser,
  setSecondaryAuthedUser,
  isUsingSecondarySession,
  usePrimarySession,
  useSecondarySession,
  isSecondaryAuthEnabled,
  SECONDARY_AUTH_SET_COOKIE_KEY,
  isPrimarySessionActive,
  isForSecondaryAuthQueryParam,
} from 'blb-utils/secondaryAuth';
import { getParsedCookieValue, removeCookie } from 'blb-utils/cookies';

export namespace App {
  export interface Props {}
}

export const App: React.FC<App.Props> = ({}) => {
  const [me, setMe] = useState<MeContext>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const locationHook = useLocation();

  const fetchMe = async () => {
    try {
      const { data } = await getMe();
      Sentry.setUser({ id: data.principalId, principalType: data.principalType });
      setMe(data);
    } catch (error) {
      // no session at all, clear all localStorage auth users
      clearAllAuthedUsers();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (getParsedCookieValue(SECONDARY_AUTH_SET_COOKIE_KEY)) {
      // server has just set secondary auth token and primary auth is active
      // We want to use the secondary session so the getMe() below can fetch secondary session user
      if (isPrimarySessionActive()) {
        useSecondarySession();
      }
      // remove this once it's used
      removeCookie(SECONDARY_AUTH_SET_COOKIE_KEY);
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchMe();
  }, []);

  useEffect(() => {
    if (!me) {
      // not logged in yet or we are at initial react state, do nothing
      return;
    }
    if (!isPrimarySessionSet() && !isSecondarySessionSet() && !isUsingSecondarySession()) {
      // initial state nothing is set, let's set primary session
      setPrimaryAuthedUser(me);
    } else if (isPrimarySessionSet() && !isSecondarySessionSet()) {
      // if only primary session is set and not secondary, let's set secondary session
      setSecondaryAuthedUser(me);
    } else if (isPrimarySessionSet() && isSecondarySessionSet()) {
      // if both primary and secondary sessions are set, we want determine which one to actually use
      if (me.principalId === getPrimarySessionPrincipalId()) {
        // use primary
        usePrimarySession();
      } else if (me.principalId === getSecondarySessionPrincipalId()) {
        // use secondary
        useSecondarySession();
      } else {
        // if it does not match any of the two, let's replace this with the secondary
        // This could happen if the user manually navigates to /login and logs in again.
        setSecondaryAuthedUser(me);
      }
    }
  }, [me]);

  const refCallback = (node: HTMLDivElement) => {
    // if we have already set the app element
    if (containerRef.current) return;
    containerRef.current = node;
    setAppElement(containerRef.current);
  };
  if (isLoading) return null;
  const isAuthed = !!me;
  const { useIdentityLoginUI } = getConfig();
  const isSecondaryLogin =
    isSecondaryAuthEnabled() &&
    (locationHook.pathname.startsWith('/login') || locationHook.pathname.startsWith('/forgot-password')) &&
    isForSecondaryAuthQueryParam() === 'true';
  const app = (() => {
    if (isAuthed) {
      if (isSecondaryLogin) {
        // This is to allow users to log in to a secondary session. For more details:
        // https://paper.dropbox.com/doc/Eng-spec-Workspace-Identity-Consolidation--BQsjxtOvC2f7PE9chx3qRbSSAg-1dpwhD8lUP5ZjHKmPzEuC
        return <UnauthenticatedApp setMe={setMe} />;
      }
      return <AuthenticatedApp me={me} />;
    }
    if (useIdentityLoginUI) {
      // Identity service UI login should always be for the primary session only.
      // In case localStorage for secondary auth session flag is not cleared, we want to force clear here.
      clearAllAuthedUsers();
      redirectToIdentityLoginUI(location.search, location.href);
      return null;
    }
    return <UnauthenticatedApp setMe={setMe} />;
  })();
  return (
    <div id="it-App" ref={refCallback}>
      <Alchemy grommetTheme={grommetTheme}>{app}</Alchemy>
    </div>
  );
};
